import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconName, IconNames } from '@icon/assets';
import { UiButtonComponent, UIIconComponent, UiInputComponent, UiNumberComponent } from '@design-system';
import { OrderSide } from '@shared-types';
import { KeyboardDirective } from '../../../../components/keyboard';
import { OrderFormFacade } from '../../data-access/order-form.facade';
import { ORDER_QUANTITY_LABEL } from '../../constants/order-calculator.constants';

import {
	calculateOrderValueFromQuantity,
	formatWithSeparator,
	parseFormattedNumber
} from '../../tools/order-calculator.tools';
import { OrderCalculatorComponent } from '../order-calculator/order-calculator.component';

/**
 * Quantity field with calculator toggle.
 * Reads/writes quantity via OrderFormFacade; holds order amount for the calculator.
 */
@Component({
	selector: 'app-order-quantity-input',
	templateUrl: './order-quantity-input.component.html',
	imports: [
		FormsModule,
		UiInputComponent,
		UIIconComponent,
		UiNumberComponent,
		OrderCalculatorComponent,
		UiButtonComponent,
		KeyboardDirective
	]
})
export class OrderQuantityInputComponent {
	private readonly orderFormFacade = inject(OrderFormFacade);

	/** Order amount (مبلغ) and panel state live on the facade — the price field reads them too. */
	readonly orderValue = this.orderFormFacade.orderValue;
	readonly calculatorOpen = this.orderFormFacade.calculatorOpen;
	readonly quantityLabel = ORDER_QUANTITY_LABEL;

	readonly side = computed(() => this.orderFormFacade.orderFormModel().side);
	readonly price = computed(() => this.orderFormFacade.orderFormModel().price ?? 0);
	readonly commission = computed(() =>
		this.side() === OrderSide.Buy
			? this.orderFormFacade.buyCommissionRate()
			: this.orderFormFacade.sellCommissionRate()
	);

	readonly minQuantity = computed(() => {
		const { volume } = this.orderFormFacade.validRanges();
		return volume.min > 0 ? volume.min : undefined;
	});
	readonly maxQuantity = computed(() => this.orderFormFacade.quantityMax());
	readonly quantity = computed(() => this.orderFormFacade.orderFormModel().quantity ?? 0);

	readonly hasError = computed(() => {
		const value = this.orderFormFacade.orderFormModel().quantity;
		// Cleared/empty field should not show validation errors.
		if (value === null || value === undefined) {
			return false;
		}
		return this.orderFormFacade.form.quantity().invalid();
	});

	readonly calculatorIcon = computed<IconNames>(() =>
		this.calculatorOpen() ? IconName.CalculatorFill : IconName.Calculator
	);

	readonly formattedQuantity = computed(() => {
		const qty = this.orderFormFacade.orderFormModel().quantity;
		// Cleared field → empty; explicit 0 (e.g. keyboard −1) → "0"
		if (qty === null || qty === undefined) {
			return '';
		}
		return formatWithSeparator(qty);
	});

	toggleCalculator(): void {
		const opening = !this.calculatorOpen();
		if (opening) {
			this.syncAmountFromQuantityIfNeeded();
		}
		this.calculatorOpen.set(opening);
	}

	onQuantityInput(raw: string | number): void {
		const next = this.parseQuantityInput(raw);
		this.writeQuantity(next);
		this.syncAmountFromQuantity(next ?? 0);
	}

	onMinQuantity(): void {
		this.fillQuantity(this.minQuantity());
	}

	onMaxQuantity(): void {
		this.fillQuantity(this.maxQuantity());
	}

	/** No-op while the symbol has reported no bound to fill. */
	private fillQuantity(bound: number | undefined): void {
		if (bound === undefined) {
			return;
		}
		this.onQuantityInput(bound);
	}

	/** On open: sell amount is derived from quantity; a buy amount is an independent budget. */
	private syncAmountFromQuantityIfNeeded(): void {
		if (this.side() !== OrderSide.Sell) {
			return;
		}
		this.syncAmountFromQuantity(this.quantity());
	}

	/**
	 * A quantity typed by the user drives the amount on both sides.
	 * Without a price the amount is unknowable, so it is cleared rather than left stale —
	 * the calculator refills it as soon as a price is entered.
	 */
	private syncAmountFromQuantity(qty: number): void {
		const price = this.price();
		if (qty <= 0 || !price) {
			this.orderValue.set(0);
			return;
		}
		this.orderValue.set(calculateOrderValueFromQuantity(this.side(), qty, price, this.commission()));
	}

	/** Empty clear → null; otherwise keep the parsed number (including 0). */
	private parseQuantityInput(raw: string | number): number | null {
		if (raw === '' || raw === null || raw === undefined) {
			return null;
		}
		return parseFormattedNumber(raw);
	}

	private writeQuantity(qty: number | null): void {
		const next = qty === null ? null : Math.max(0, qty);
		if (this.orderFormFacade.orderFormModel().quantity === next) {
			return;
		}
		this.orderFormFacade.setFormData({ quantity: next });
	}
}
